import React, { useState } from 'react'
import { Link, Box, Dialog, Typography, styled } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import TriggerRenderer from './TriggerRenderer'
import { useTheme } from '@mui/material/styles'

// Create styled components for the modal
const ModalHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    maxHeight: '12%',
    padding: '2.8rem 1.2rem 1.6rem 2.8rem',
    backgroundColor: theme.palette.background.paper,
}))

const ModalTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1.8rem',
    fontWeight: 'bold',
    color: theme?.palette?.text?.primary,
}))

const ModalBody = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    padding: '2.4rem 4rem',
    '&:focus': {
        outline: 'none',
    },
}))

const ModalFooter = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
}))

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        borderRadius: '1rem',
        maxWidth: '80rem',
        '@media (max-width: 767px)': {
            margin: '0.8rem',
            width: 'calc(100% - 1.6rem) !important',
            maxWidth: 'calc(100% - 1.6rem) !important',
        },
    },
})

const CloseIconWrapper = styled(Box)({
    display: 'flex',
    height: '1.3rem',
    marginTop: '-1.3rem',
})

/**
 * CustomMenuModal component that renders a modal with custom HTML content
 *
 * @param {Object} props - Component props
 * @param {Object} props.menuItem - Configuration for the menu item
 * @param {Object} props.menuItem.trigger - Trigger configuration
 * @param {string} props.menuItem.content - HTML content for the modal
 * @param {Object} props.menuItem.modalOptions - Optional configuration for the modal
 * @param {string} props.menuItem.modalOptions.title - Title of the modal
 * @param {string} props.menuItem.modalOptions.closeIconColor - Color of the close icon (default: '#727272')
 * @param {string} props.menuItem.modalOptions.footerContent - HTML content for the footer
 * @param {string} props.menuItem.modalOptions.footerAlign - Alignment of the footer content
 * @param {boolean} props.menuItem.modalOptions.disableClose - Whether to disable the close button
 */
const CustomMenuModal = ({ menuItem }) => {
    const [open, setOpen] = useState(false)
    const theme = useTheme()
    if (!menuItem || !menuItem.trigger || !menuItem.content) return null

    const { trigger, content, modalOptions = {} } = menuItem

    const handleOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    // Default title if not provided in modalOptions
    const title = modalOptions.title || 'Information'
    const disableClose = modalOptions.disableClose || false

    return (
        <>
            <Link
                underline="none"
                component="button"
                onClick={handleOpen}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                }}
            >
                <TriggerRenderer trigger={trigger} />
            </Link>

            <StyledDialog
                open={open}
                onClose={disableClose ? null : handleClose}
                aria-labelledby={title}
                aria-describedby={title}
                sx={modalOptions.customDialogClass}
                scroll="body"
            >
                <ModalHeader>
                    <ModalTitle>{title}</ModalTitle>
                    {!disableClose && (
                        <CloseIconWrapper sx={{ marginTop: '0' }}>
                            <Box
                                onClick={handleClose}
                                sx={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0.8rem',
                                }}
                            >
                                <Icon
                                    style={{
                                        height: '1.3rem',
                                        width: '1.3rem',
                                        color:
                                            modalOptions.closeIconColor ||
                                            theme?.palette?.text.secondary,
                                    }}
                                    name="Close"
                                />
                            </Box>
                        </CloseIconWrapper>
                    )}
                </ModalHeader>

                <ModalBody>
                    <Box
                        sx={{
                            height: '100%',
                            overflow: 'auto',
                        }}
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                </ModalBody>

                {modalOptions.footerContent && (
                    <ModalFooter>
                        <Box
                            sx={{
                                padding: '1.6rem 2.4rem',
                                display: 'flex',
                                justifyContent:
                                    modalOptions.footerAlign || 'flex-end',
                                gap: '1.2rem',
                            }}
                            dangerouslySetInnerHTML={{
                                __html: modalOptions.footerContent,
                            }}
                        />
                    </ModalFooter>
                )}
            </StyledDialog>
        </>
    )
}

export default CustomMenuModal
